import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import clsx from 'clsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/Table';
import type { MetricColumnDef } from 'app/modules/analytics/modules/content/utils/contentTableColumns';
import type { ContentPostTableRow } from 'app/modules/analytics/modules/content/types/contentTableRow';
import {
  ContentPreviewCell,
  MetricCellValue,
  PostTypeCell,
} from 'app/modules/analytics/modules/content/components/contentTableCells';

interface ReportContentPostTableProps {
  title: string;
  columns: MetricColumnDef[];
  rows: ContentPostTableRow[];
  isLoading?: boolean;
}

const headerCellClass = 'tw-align-top';
const skeletonRows = 5;

const ReportContentPostTable: FC<ReportContentPostTableProps> = ({
  title,
  columns,
  rows,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const totalColCount = 3 + columns.length;

  return (
    <section className="tw-flex tw-w-full tw-flex-col tw-gap-3">
      <h3 className="tw-text-md tw-font-semibold tw-text-grayscale-180">
        {title}
      </h3>
      <div className="tw-rounded-lg tw-border tw-border-grayscale-20 tw-bg-white">
        <Table className="tw-table-fixed tw-border-collapse tw-text-left tw-text-xs tw-leading-4">
          <TableHeader>
            <TableRow className="tw-bg-white hover:tw-bg-white">
              <TableHead className={clsx(headerCellClass, 'tw-w-[22%]')}>
                {t('analyticsContent.preview')}
              </TableHead>
              <TableHead className={clsx(headerCellClass, 'tw-w-[7%]')}>
                {t('analyticsContent.type')}
              </TableHead>
              <TableHead className={clsx(headerCellClass, 'tw-w-[10%]')}>
                {t('analyticsContent.date')}
              </TableHead>
              {columns.map((col) => (
                <TableHead key={col.key} className={headerCellClass}>
                  <span className="tw-min-w-0 tw-whitespace-normal tw-break-words tw-leading-tight">
                    {t(col.labelKey)}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: skeletonRows }).map((_, index) => (
                  <TableRow
                    key={`sk-${index}`}
                    className="hover:tw-bg-transparent"
                  >
                    <TableCell colSpan={totalColCount}>
                      <Skeleton className="tw-h-10 tw-w-full" />
                    </TableCell>
                  </TableRow>
                ))
              : null}
            {!isLoading && rows.length === 0 ? (
              <TableRow className="hover:tw-bg-transparent">
                <TableCell
                  className="tw-py-8 tw-text-center tw-text-sm tw-text-grayscale-90"
                  colSpan={totalColCount}
                >
                  {t('analyticsContent.noPosts')}
                </TableCell>
              </TableRow>
            ) : null}
            {!isLoading
              ? rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <ContentPreviewCell row={row} size="compact" />
                    </TableCell>
                    <TableCell>
                      <PostTypeCell type={row.postType} />
                    </TableCell>
                    <TableCell className="tw-text-grayscale-180">
                      {row.dateLabel}
                    </TableCell>
                    {columns.map((col) => {
                      const cell = row.metrics[col.key];
                      return (
                        <TableCell key={col.key}>
                          {cell ? (
                            <MetricCellValue
                              value={cell.display}
                              highlight={cell.highlight}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              : null}
          </TableBody>
        </Table>
      </div>
    </section>
  );
};

export default ReportContentPostTable;
